import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { useRef } from 'react';
import { useProgress } from '@react-three/drei';
import { SplitText } from 'gsap/SplitText';
import React from 'react';
import useGameStore from '../../store/useGameStore';
import { useIsMobile } from '../../utils/use-mobile';

gsap.registerPlugin(useGSAP, SplitText);

type ButtonProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  children: React.ReactNode;
};

export const Interface = () => {
  const container = useRef(null!);

  const { progress, active } = useProgress();
  const setPlayAudio = useGameStore((state) => state.setPlayAudio);

  const overlay = gsap.utils.selector(container);

  const Button = ({ children, ...props }: ButtonProps) => {
    return (
      <button
        className='relative text-lg group hover:cursor-pointer will-change-transform'
        {...props}
      >
        <span className='relative z-10 block px-5 py-3 overflow-hidden font-medium leading-tight text-gray-800 transition-colors duration-300 ease-out border-2 border-gray-900 rounded-lg group-hover:text-white'>
          <span className='absolute inset-0 w-full h-full px-5 py-3 rounded-lg bg-neutral-50'></span>
          <span className='absolute left-0 w-48 h-48 -ml-2 transition-all duration-300 origin-bottom translate-y-1/2 bg-neutral-900 group-hover:-translate-y-1/4 ease'></span>
          <span className='relative'>{children}</span>
        </span>
        <span
          className='absolute bottom-0 right-0 w-full h-12 -mb-1 -mr-1 transition-all duration-200 ease-linear bg-neutral-900 rounded-lg group-hover:mb-0 group-hover:mr-0'
          data-rounded='rounded-lg'
        ></span>
      </button>
    );
  };

  const { contextSafe } = useGSAP(
    () => {
      const tl = gsap.timeline({ paused: true });

      tl.set(container.current, { autoAlpha: 1 });

      tl.to(overlay('.text-path'), {
        attr: {
          startOffset: '0',
        },
        duration: 1.5,
        ease: 'power1.out',
      });

      tl.from(
        overlay('#button-group > button'),

        {
          yPercent: 120,
          ease: 'power1.inOut',
          onComplete: () => {
            tl.to(overlay('#button-group > button:first-child'), {
              scale: 1.03,
              repeat: -1,
              duration: 0.6,
              yoyo: true,
              ease: 'power1.inOut',
            });
          },
        }
      );

      const eplipsSplit = SplitText.create(overlay('#elips'));

      gsap
        .to(eplipsSplit.chars, {
          stagger: 0.1,
          yPercent: 10,
          repeat: -1,
          yoyo: true,
        })
        .paused(!active);

      if (!active) {
        tl.play().to(overlay(' #loading-text>p'), {
          yPercent: 100,
          duration: 0.6,
          delay: 0.5,
          stagger: 0.08,
          ease: 'power1',
        });
      }
    },
    { scope: container, dependencies: [active] }
  );

  const handleClick = contextSafe(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const audioState = e.currentTarget.textContent === 'Enter with audio';

      const tl = gsap.timeline();
      tl.to(e.currentTarget, {
        scale: 0.9,
        duration: 0.2,
      });

      tl.to(e.currentTarget, {
        scale: 1,
        duration: 0.2,
      });

      tl.to(container.current, {
        opacity: 0,
        ease: 'circ',
      });
      tl.set(container.current, { display: 'none' });
      tl.call(() => {
        if (audioState) {
          setPlayAudio(true);
        }
      });
    }
  );

  const isMobile = useIsMobile();

  return (
    <>
      {isMobile && (
        <div className='z-20 fixed top-0 left-0 w-full h-dvh flex bg-[#0f0f2e] items-center justify-center gap-5 flex-col'>
          <p className='font-barriecito text-white text-xl text-wrap'>
            Sorry we dont support mobile devices yet ☹️
          </p>
          <p
            id='blink'
            className='font-barriecito text-white text-2xl animate-pulse capitalize'
          >
            Open on PC for better experience
          </p>
        </div>
      )}
      <main
        ref={container}
        className='h-dvh font-barriecito w-full invisible bg-none'
      >
        <div
          id='intro'
          className='relative bg-[#0f0f2ee9]  text-[57px] text-neutral-200  h-full w-full flex items-center justify-center'
        >
          <svg className='max-w-[1000px] w-full ' viewBox='0 50 500 300'>
            <defs>
              <path
                id='text-path-0'
                d='M 100 310.195 C 100 100 400 100 400 300'
              ></path>
            </defs>
            <text
              className='fill-neutral-200'
              style={{
                fontFamily: 'Barriecito',
                fontSize: '57px',
                letterSpacing: '-6.5px',
                textTransform: 'uppercase',
                wordSpacing: '1.3px',
                whiteSpace: 'pre',
                textDecoration: 'underline overline',
              }}
            >
              <textPath
                className='text-path'
                href='#text-path-0'
                startOffset='500'
              >
                WELCOME TO ROY'S ISLAND
              </textPath>
            </text>
          </svg>
          <div
            id='button-group'
            className='flex p-2 overflow-hidden max-w-2xl gap-4 text-2xl absolute top-[70%] text-neutral-800 left-[50%] -translate-x-[50%]'
          >
            <Button onClick={(e) => handleClick(e)}>Enter with audio</Button>
            <Button onClick={(e) => handleClick(e)}>Enter</Button>
          </div>

          <div
            id='loading-text'
            className='absolute flex justify-between items-baseline bottom-10 text-7xl overflow-hidden h-fit w-full px-4 '
          >
            <p className='flex items-baseline italic'>
              Loading
              <span id='elips' className='text-9xl'>
                ...
              </span>
            </p>

            <p className='flex items-baseline  italic '>
              {progress.toPrecision(4)}%
            </p>
          </div>
        </div>
      </main>
    </>
  );
};
